import React, { useRef, useState, useEffect } from "react";
import {
  PlayCircleOutline as PlayIcon,
  ErrorOutline as ErrorIcon,
} from "@mui/icons-material";
import makeStyles from "@mui/styles/makeStyles";

import useNexusVideoSession from "./useNexusVideoSession";
import useIdleStop from "./useIdleStop";

const useStyles = makeStyles((theme) => ({
  player: {
    maxWidth: "100%",
    objectFit: "contain",
    backgroundColor: "#000",
    display: "block",
  },
  root: {
    margin: 0,
    position: "relative",
    width: "100%",
  },
  overlay: {
    display: "grid",
    placeItems: "center",
    top: "30%",
    bottom: "30%",
    left: "35%",
    right: "35%",
    position: "absolute",
    textAlign: "center",
    background: "rgba(0,0,0,0.85)",
    borderRadius: theme.spacing(2),
    cursor: "pointer",
    zIndex: 1,
    color: "#fff",
    "&:hover": {
      background: `radial-gradient(#2e2e2e, rgba(0,0,0,0.85))`,
    },
  },
  playIcon: {
    width: "90%",
    height: "90%",
  },
  idleWarning: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    textAlign: "center",
    color: "#fff",
    background: "rgba(0,0,0,0.7)",
    padding: theme.spacing(0.5),
    fontSize: "0.85rem",
    zIndex: 1,
  },
  statusText: {
    fontSize: "0.85rem",
    marginTop: theme.spacing(1),
  },
}));

const IDLE_TIMEOUT = 2 * 60 * 1000;
const IDLE_WARNING = 30 * 1000;

const VehiclePlaybackNexusWebRtc = ({
  vehicleId,
  cameraId,
  playFrom,
  play,
  onPlayClick,
  onPauseClick,
}) => {
  const classes = useStyles();
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);

  // Fixed pixel box computed 16:9 from the wrapper width (same approach as the CMSv6 player). This is
  // what stops the jump: the <video> gets explicit width/height in pixels, so it never falls back to
  // its intrinsic size and never grows when the first frame arrives or the stream reconnects.
  const [videoWH, setVideoWH] = useState({ w: 0, h: 0 });

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return undefined;

    const calc = () => {
      const rect = wrapper.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.max(1, Math.round(w / (16 / 9)));
      setVideoWH((prev) => (prev.w !== w || prev.h !== h ? { w, h } : prev));
    };

    let frame = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(calc);
    });
    ro.observe(wrapper);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, []);

  const [restartKey, setRestartKey] = useState(0);

  const { state, statusText } = useNexusVideoSession({
    mode: "playback",
    vehicleId,
    cameraId,
    playFrom,
    videoRef,
    play,
    restartKey,
  });

  const handleVideoPause = () => {
    if (videoRef.current?.srcObject) onPauseClick?.();
  };

  const idleSecondsLeft = useIdleStop({
    timeout: IDLE_TIMEOUT,
    warnFor: IDLE_WARNING,
    onStop: () => {
      if (play) onPauseClick?.();
    },
  });

  return (
    <figure className={classes.root} ref={wrapperRef}>
      {play &&
        (state === "live" || state === "connecting") &&
        idleSecondsLeft !== null && (
          <div className={classes.idleWarning}>
            Playback will stop in {idleSecondsLeft}s due to inactivity
          </div>
        )}

      {!play && (
        <div className={classes.overlay} onClick={onPlayClick}>
          <PlayIcon className={classes.playIcon} />
        </div>
      )}

      {play && state === "error" && (
        <div className={classes.overlay} onClick={onPauseClick}>
          <div>
            <ErrorIcon style={{ fontSize: 48 }} />
            <div className={classes.statusText}>{statusText}</div>
          </div>
        </div>
      )}

      {play && state === "connecting" && (
        <div className={classes.overlay} onClick={onPauseClick}>
          <div className={classes.statusText}>{statusText}</div>
        </div>
      )}

      {play && state === "idle" && (
        <div
          className={classes.overlay}
          onClick={() => setRestartKey((key) => key + 1)}
        >
          <div>
            <PlayIcon className={classes.playIcon} />
            <div className={classes.statusText}>{statusText}</div>
          </div>
        </div>
      )}

      <video
        className={classes.player}
        ref={videoRef}
        style={{
          width: videoWH.w || "100%",
          height: videoWH.h || undefined,
        }}
        playsInline
        autoPlay
        muted
        controls
        onPause={handleVideoPause}
        onVolumeChange={(e) => {
          e.target.muted = true;
        }}
      >
        Sorry, your browser doesn&apos;t support embedded videos.
      </video>
    </figure>
  );
};

export default VehiclePlaybackNexusWebRtc;
